// ============================================================================
// QUOTE HERO PERSISTENCE
// ----------------------------------------------------------------------------
// Stage 5 renders a data URL so the builder can crop/preview the cover image,
// but customer media bytes must never be written inside the project JSON row.
// This seam materialises a manual cover selection as an ordinary project photo
// before the existing photo blob/S3 pipeline makes it portable.
// ============================================================================

import { compressIngestedImage } from './image';

export interface QuoteHeroDisplayBinding {
    displayValue: unknown;
    sourcePhoto: Record<string, unknown> | null;
    automatic: boolean;
}

export interface QuoteHeroDisplayBindingOptions {
    automatic?: boolean;
}

export interface CanAutoSelectHeroOptions {
    currentHero?: unknown;
    autoSelectionSuppressed?: boolean;
    currentBinding?: QuoteHeroDisplayBinding;
}

export interface ApplyQuoteHeroSelectionOptions {
    suppressAutoSelection?: boolean;
}

export interface AdoptResolvedCandidateOptions {
    currentHero?: unknown;
    compressedHero?: unknown;
    sourcePhoto?: Record<string, unknown> | null;
    autoSelectionSuppressed?: boolean;
    currentBinding?: QuoteHeroDisplayBinding;
    automatic?: boolean;
}

export interface AdoptCandidateResult {
    adopted: boolean;
    binding: QuoteHeroDisplayBinding;
}

export interface PrepareQuoteHeroOptions {
    quoteData?: Record<string, unknown> | null;
    selectedHeroImage?: unknown;
    photos?: unknown[];
    compressImage?: typeof compressIngestedImage;
}

export interface PrepareQuoteHeroResult {
    quoteData: unknown;
    selectedHeroImage: unknown;
    photos: unknown[];
}

function isObject(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isImageDataUrl(value: unknown): boolean {
    return typeof value === 'string' && /^data:image\/[a-z0-9.+-]+;base64,/i.test(value);
}

function isSessionImageUrl(value: unknown): boolean {
    return isImageDataUrl(value)
        || (typeof value === 'string' && value.startsWith('blob:'));
}

function inlinePhotoData(photo: unknown): string | null {
    if (!isObject(photo)) return null;
    return ([photo.data, photo.dataUrl, photo.originalData]
        .find(isImageDataUrl) as string) || null;
}

function hasDurablePhotoReference(photo: unknown): boolean {
    return isObject(photo) && !!(photo.s3Key || photo.fileMetadataId || photo.blobId);
}

export function createQuoteHeroDisplayBinding(displayValue: unknown, sourcePhoto: unknown = null, {
    automatic = false,
}: QuoteHeroDisplayBindingOptions = {}): QuoteHeroDisplayBinding {
    return {
        displayValue,
        sourcePhoto: isObject(sourcePhoto) ? sourcePhoto : null,
        automatic: automatic === true,
    };
}

function sameHeroSource(left: unknown, right: unknown): boolean {
    if (left === right) return true;
    if (!isObject(left) || !isObject(right)) return false;
    const keys = ['id', 'blobId', 's3Key', 's3_key', 'fileMetadataId', 'file_metadata_id', 'url'];
    return keys.some(key => left[key] != null
        && right[key] != null
        && String(left[key]) === String(right[key]));
}

function heroLaneIsEmpty(value: unknown): boolean {
    return value == null || value === '';
}

function canonicalHeroLanesMatch(left: unknown, right: unknown): boolean {
    if (heroLaneIsEmpty(left) && heroLaneIsEmpty(right)) return true;
    return sameHeroSource(left, right);
}

/**
 * Normalize the post-save project receipt to one authoritative quote hero.
 * `quoteData.header.heroImage` owns the final quote presentation and the
 * project-level selected lane is its derived lookup/cache. A receipt with only
 * one lane is repaired in memory. Two populated, different identities are an
 * unknown save outcome and must not be painted as success.
 */
export function adoptCanonicalQuoteHeroReceipt<T extends Record<string, unknown>>(savedProject: T): T {
    if (!isObject(savedProject)) return savedProject;
    const receivedQuote = (savedProject.quoteData || savedProject.quote_data || null) as Record<string, unknown> | null;
    if (!isObject(receivedQuote)) return savedProject;
    const header = isObject(receivedQuote.header) ? receivedQuote.header as Record<string, unknown> : {};
    const hasHeaderHero = Object.prototype.hasOwnProperty.call(header, 'heroImage');
    const hasSelectedHero = Object.prototype.hasOwnProperty.call(savedProject, 'selectedHeroImage')
        || Object.prototype.hasOwnProperty.call(savedProject, 'selected_hero_image');
    const selectedHero = hasSelectedHero
        ? (savedProject.selectedHeroImage ?? savedProject.selected_hero_image ?? null)
        : null;

    if (hasHeaderHero && hasSelectedHero && !canonicalHeroLanesMatch(header.heroImage, selectedHero)) {
        const error: any = new Error('Project save returned different quote-header and selected hero images.');
        error.code = 'QUOTE_HERO_RECEIPT_DIVERGED';
        error.retryable = true;
        throw error;
    }

    if (!hasHeaderHero && !hasSelectedHero) return savedProject;
    const canonicalHero = hasHeaderHero ? header.heroImage : selectedHero;
    const canonicalQuote = hasHeaderHero
        ? receivedQuote
        : {
            ...receivedQuote,
            header: {
                ...header,
                heroImage: canonicalHero,
            },
        };
    return {
        ...savedProject,
        quoteData: canonicalQuote,
        ...(Object.prototype.hasOwnProperty.call(savedProject, 'quote_data')
            ? { quote_data: canonicalQuote }
            : {}),
        selectedHeroImage: canonicalHero,
        ...(Object.prototype.hasOwnProperty.call(savedProject, 'selected_hero_image')
            ? { selected_hero_image: canonicalHero }
            : {}),
    };
}

/**
 * A populated cover normally represents stored/operator authority and is never
 * eligible for automatic replacement. The one exception is a display value
 * that this open Stage 5 session itself marked automatic. That distinction lets
 * late image-analysis metadata replace an early first-file fallback without
 * ever taking a deliberate builder choice away.
 */
export function canAutomaticallySelectQuoteHero({
    currentHero,
    autoSelectionSuppressed = false,
    currentBinding = createQuoteHeroDisplayBinding(null),
}: CanAutoSelectHeroOptions = {}): boolean {
    if (autoSelectionSuppressed) return false;
    if (!currentHero) return true;
    return currentBinding?.automatic === true
        && currentBinding?.displayValue === currentHero;
}

export function applyQuoteHeroSelectionState<T extends Record<string, unknown>>(quoteData: T, heroImage: unknown, {
    suppressAutoSelection = false,
}: ApplyQuoteHeroSelectionOptions = {}): T {
    if (!isObject(quoteData)) return quoteData;
    return {
        ...quoteData,
        header: {
            ...((quoteData.header as Record<string, unknown>) || {}),
            heroImage,
            heroImageAutoSelectionSuppressed: suppressAutoSelection === true,
        },
    };
}

/**
 * Carry the durable project-level cover into an older Stage 5 snapshot only
 * when that snapshot has no cover of its own. A builder's explicit Remove is
 * represented by the suppression marker and must remain authoritative.
 */
export function reconcileQuoteHeroFromProjectSelection<T extends Record<string, unknown>>(quoteData: T, selectedHeroImage: unknown): T {
    if (!isObject(quoteData) || !selectedHeroImage) return quoteData;
    const header = isObject(quoteData.header) ? quoteData.header as Record<string, unknown> : {};
    if (header.heroImage || header.heroImageAutoSelectionSuppressed === true) return quoteData;
    return {
        ...quoteData,
        header: {
            ...header,
            heroImage: selectedHeroImage,
        },
    };
}

export function adoptResolvedQuoteHeroCandidate({
    currentHero,
    compressedHero,
    sourcePhoto = null,
    autoSelectionSuppressed = false,
    currentBinding = createQuoteHeroDisplayBinding(null),
    automatic = true,
}: AdoptResolvedCandidateOptions = {}): AdoptCandidateResult {
    if (!canAutomaticallySelectQuoteHero({
        currentHero,
        autoSelectionSuppressed,
        currentBinding,
    }) || (currentHero && sameHeroSource(currentBinding?.sourcePhoto, sourcePhoto))) {
        return { adopted: false, binding: currentBinding };
    }
    return {
        adopted: true,
        binding: createQuoteHeroDisplayBinding(compressedHero, sourcePhoto, { automatic }),
    };
}

export function rebindQuoteHeroSourceForPersistence<T extends Record<string, unknown>>(quoteData: T, binding: QuoteHeroDisplayBinding | null | undefined): T {
    const sourcePhoto = binding?.sourcePhoto;
    if (!isObject(quoteData)
        || !isObject(sourcePhoto)
        || (quoteData?.header as Record<string, unknown>)?.heroImage !== binding?.displayValue) return quoteData;
    return {
        ...quoteData,
        header: {
            ...((quoteData.header as Record<string, unknown>) || {}),
            heroImage: sourcePhoto,
        },
    };
}

function stableHeroHash(value: unknown): string {
    // FNV-1a is sufficient here: this is a stable project-local identity, not a
    // security digest. Include length so common prefixes cannot collapse.
    let hash = 0x811c9dc5;
    const text = String(value || '');
    for (let index = 0; index < text.length; index += 1) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return `${(hash >>> 0).toString(36)}-${text.length.toString(36)}`;
}

function quoteHeroDurabilityError(message: string, cause: unknown = null): Error {
    const error: any = new Error(message);
    error.code = 'QUOTE_HERO_DURABILITY_REQUIRED';
    error.retryable = true;
    if (cause) error.cause = cause;
    return error;
}

function findMatchingProjectPhoto(heroImage: unknown, photos: unknown): Record<string, unknown> | null {
    const rows = (Array.isArray(photos) ? photos : []) as Array<Record<string, unknown>>;
    if (isObject(heroImage)) {
        if (heroImage.id != null) {
            const byId = rows.find(photo => photo?.id != null
                && String(photo.id) === String(heroImage.id));
            if (byId) return byId;
        }
        for (const name of [heroImage.name, heroImage.originalName, heroImage.fileName]) {
            if (!name) continue;
            const byName = rows.find(photo => [photo?.name, photo?.originalName, photo?.fileName]
                .some(candidate => candidate && String(candidate) === String(name)));
            if (byName) return byName;
        }
    }
    const inline = isImageDataUrl(heroImage) ? heroImage : inlinePhotoData(heroImage);
    if (!inline) return null;
    return rows.find(photo => inlinePhotoData(photo) === inline) || null;
}

function upsertPhoto<T extends Record<string, unknown>>(photos: unknown, photo: T): T[] {
    const rows = (Array.isArray(photos) ? photos : []) as T[];
    const index = rows.findIndex(candidate => candidate?.id != null
        && photo?.id != null
        && String(candidate.id) === String(photo.id));
    if (index < 0) return [...rows, photo];
    const next = [...rows];
    next[index] = { ...rows[index], ...photo };
    return next;
}

/**
 * Resolve the exact portable hero acknowledged by an assignment save. The
 * immediate UI may have used compressed display bytes, but downstream quote
 * generation must receive only the canonical saved photo reference.
 */
export function resolveCanonicalSavedHero(savedProject: Record<string, unknown> | null | undefined, selectedReference: unknown = null): unknown {
    if (!isObject(savedProject)) return null;
    const hasTopLevelHero = Object.prototype.hasOwnProperty.call(savedProject, 'selectedHeroImage')
        || Object.prototype.hasOwnProperty.call(savedProject, 'selected_hero_image');
    const topLevelHero = hasTopLevelHero
        ? (savedProject.selectedHeroImage ?? savedProject.selected_hero_image ?? null)
        : null;
    if (hasDurablePhotoReference(topLevelHero)) return topLevelHero;

    const matchingPhoto = findMatchingProjectPhoto(
        selectedReference || topLevelHero,
        Array.isArray(savedProject.photos) ? savedProject.photos : [],
    );
    return hasDurablePhotoReference(matchingPhoto) ? matchingPhoto : null;
}

/**
 * Prepare a Stage 5 quote save without leaking its display data URL to cloud
 * JSON. The returned photo may still contain local bytes; useProjectPersistence
 * immediately migrates/uploads it and replaces both hero lanes with the same
 * portable catalog reference before the network write.
 */
export async function prepareQuoteHeroForProjectSave({
    quoteData,
    selectedHeroImage = null,
    photos = [],
    compressImage = compressIngestedImage,
}: PrepareQuoteHeroOptions = {}): Promise<PrepareQuoteHeroResult> {
    if (!isObject(quoteData)) {
        return { quoteData, selectedHeroImage, photos: Array.isArray(photos) ? photos : [] };
    }
    const header = isObject(quoteData.header) ? quoteData.header as Record<string, unknown> : {};
    if (!Object.prototype.hasOwnProperty.call(header, 'heroImage')) {
        return { quoteData, selectedHeroImage, photos: Array.isArray(photos) ? photos : [] };
    }

    const heroImage = header.heroImage;
    if (heroImage == null || heroImage === '') {
        return {
            quoteData: { ...quoteData, header: { ...header, heroImage: '' } },
            selectedHeroImage: null,
            photos: Array.isArray(photos) ? photos : [],
        };
    }

    // A server/file URL is already portable and was historically accepted by
    // the cloud boundary. Only session-owned data/blob URLs need to be turned
    // into a project photo here.
    if (typeof heroImage === 'string' && !isSessionImageUrl(heroImage)) {
        return {
            quoteData,
            selectedHeroImage: heroImage,
            photos: Array.isArray(photos) ? photos : [],
        };
    }

    const matchingPhoto = findMatchingProjectPhoto(heroImage, photos);
    if (matchingPhoto && (hasDurablePhotoReference(matchingPhoto) || inlinePhotoData(matchingPhoto))) {
        return {
            quoteData: { ...quoteData, header: { ...header, heroImage: matchingPhoto } },
            selectedHeroImage: matchingPhoto,
            photos: Array.isArray(photos) ? photos : [],
        };
    }

    if (hasDurablePhotoReference(heroImage)) {
        return {
            quoteData: { ...quoteData, header: { ...header, heroImage } },
            selectedHeroImage: heroImage,
            photos: upsertPhoto(photos, heroImage as Record<string, unknown>),
        };
    }

    const inline = isSessionImageUrl(heroImage) ? heroImage : inlinePhotoData(heroImage);
    if (!inline) {
        throw quoteHeroDurabilityError(
            'Quote hero save blocked because the selected cover image has no durable photo or blob source.',
        );
    }

    let prepared: any;
    try {
        prepared = await compressImage(inline as string | Blob | File, 'quote-hero.jpg', (Array.isArray(photos) ? photos.length : 0) + 1);
    } catch (cause) {
        throw quoteHeroDurabilityError(
            'Quote hero save blocked because the selected cover image could not be prepared for durable storage.',
            cause,
        );
    }
    if (!prepared?.data || !isImageDataUrl(prepared.data) || !prepared.cloudMedia) {
        throw quoteHeroDurabilityError(
            'Quote hero save blocked because the selected cover image could not be prepared for durable storage.',
        );
    }

    const id = `quote-hero-${stableHeroHash(prepared.data)}`;
    const contentType = /^data:([^;,]+)/i.exec(prepared.data)?.[1]?.toLowerCase() || 'image/jpeg';
    const heroPhoto = {
        id,
        name: `Quote_Hero_${id.slice('quote-hero-'.length)}.jpg`,
        originalName: 'quote-hero.jpg',
        contentType,
        data: prepared.data,
        sizeKB: prepared.sizeKB,
        width: prepared.width,
        height: prepared.height,
        cloudMedia: prepared.cloudMedia,
        addedFrom: 'stage5-hero',
    };

    return {
        quoteData: { ...quoteData, header: { ...header, heroImage: heroPhoto } },
        selectedHeroImage: heroPhoto,
        photos: upsertPhoto(photos, heroPhoto),
    };
}

export const quoteHeroPersistenceInternals = Object.freeze({
    findMatchingProjectPhoto,
    hasDurablePhotoReference,
    stableHeroHash,
});
